import {
	Currencies,
	DemurrageTerms,
	HIIFieldTypes,
	InvoiceTemplates,
	LaytimeIntervals,
	LaytimeTerms,
	PnlGroups,
} from '@shared/utils/constants';
import TemplateItem from '@shared/TemplateItem';
import { Values } from '@shared/utils/objectEnums';
import { round } from '@shared/utils/math';
import {
	dndRateUnitLabel,
	hoursToInterval,
	intervalTimeUnit,
} from '@shared/utils/laytime';
import HireInvoiceItemModel from '@api/models/hire-invoice-item';
import type Cargo from '@api/models/cargo';
import type CargoPort from '@api/models/cargo-port';
import type { HIIDespatchType } from '@api/models/hii-despatch';
import { Port } from '@api/utils/ports';
import { formatCurrency } from '../utils/currency';
import HireInvoiceItem, {
	HIIConstructorParams,
	TemplateItemChildren,
} from './HireInvoiceItem';

class HIIDespatch extends HireInvoiceItem {
	static get itemType() {
		return 'despatch';
	}

	cargoId: number;
	cargoPortId: number | null;
	amount: number;
	hours: number;
	rate: number;
	charterer: number | null;
	currency: Values<typeof Currencies>;
	terms: LaytimeTerms;
	demurrageTerm: DemurrageTerms;
	type: 'loading' | 'discharging' | null;
	laytimeDetails: TemplateItemChildren | null;
	port: Port | null;
	demurrageDespatchInterval: LaytimeIntervals;
	timeAllowedInterval: LaytimeIntervals;
	isSettlement: boolean;

	constructor(
		id: number,
		isOriginal: boolean,
		params: HIIConstructorParams & {
			cargoId: number;
			cargoPortId: number | null;
			amount: number;
			hours: number;
			rate: number;
			charterer: number | null;
			currency: Values<typeof Currencies>;
			terms: LaytimeTerms;
			demurrageTerm?: DemurrageTerms;
			type: 'loading' | 'discharging' | null;
			laytimeDetails: TemplateItemChildren | null;
			port: Port | null;
			demurrageDespatchInterval?: LaytimeIntervals | null;
			timeAllowedInterval?: LaytimeIntervals | null;
			isSettlement?: boolean;
		},
	) {
		super(id, isOriginal, { fixtureCurrency: params.fixtureCurrency });

		this.cargoId = params.cargoId;
		this.cargoPortId = params.cargoPortId;
		this.amount = params.amount;
		this.hours = params.hours;
		this.rate = params.rate;
		this.charterer = params.charterer;
		this.currency = params.currency;
		this.terms = params.terms;
		this.demurrageTerm = params.demurrageTerm ?? DemurrageTerms.DEMURRAGE;
		this.type = params.type;
		this.laytimeDetails = params.laytimeDetails;
		this.port = params.port;
		this.demurrageDespatchInterval = params.demurrageDespatchInterval ?? LaytimeIntervals.DAY;
		this.timeAllowedInterval = params.timeAllowedInterval ?? LaytimeIntervals.DAY;
		this.isSettlement = params.isSettlement ?? false;
		this.pnlGroup = PnlGroups.DEMURRAGE_DESPATCH;
		this._showTotal = true;
		this.itemTypeLabel = 'Despatch';
		this.addManually = true;
		this.templateSection = TemplateItem.Sections.DND.replace('Demurrage', this.demurrageTerm);

		this._fields = {
			hours: {
				label: 'Hours',
				type: HIIFieldTypes.NUMBER,
			},
		};
	}

	_calculateTotal() {
		return round(this.amount, 2);
	}

	getTimeValue() {
		return hoursToInterval(this.hours, this.timeAllowedInterval);
	}

	getTimeUnit() {
		return intervalTimeUnit(this.timeAllowedInterval, this.getTimeValue());
	}

	getRateUnitLabel() {
		if (this.isSettlement) {
			return '';
		}

		return dndRateUnitLabel(this.demurrageDespatchInterval);
	}

	getDescription() {
		if (this.isSettlement) {
			return 'Settlement';
		}

		const timeStr = `${this.getTimeValue()} ${this.getTimeUnit()} gained at `;
		const rate = formatCurrency(round(this.rate, 2), this.fixtureCurrency);

		return `${this.terms !== LaytimeTerms.AVERAGED ? timeStr : ''}${rate} ${this.getRateUnitLabel()}`;
	}

	getDays() {
		return hoursToInterval(this.hours, LaytimeIntervals.DAY);
	}

	static async _getClassParams(model: HIIDespatchType, _parentModel: HireInvoiceItemModel) {
		const cargo = await model.getCargo({
			paranoid: false,
			include: model.cargoPortId == null ? [] : [{
				association: 'CargoPorts',
				where: { id: model.cargoPortId },
				required: false,
				paranoid: false,
			}],
		}) as (Cargo & { CargoPorts: CargoPort[] }) | null;

		return {
			cargoId: model.cargoId,
			currency: cargo?.currency,
			charterer: cargo?.charterer ?? null,
			port: cargo?.CargoPorts?.[0]?.port ?? null,
			cargoPortId: model.cargoPortId,
			amount: model.amount,
			terms: model.terms,
			hours: model.hours,
			type: model.type,
			rate: model.rate,
			demurrageDespatchInterval: model.demurrageDespatchInterval,
			timeAllowedInterval: model.timeAllowedInterval,
			isSettlement: model.isSettlement,
		};
	}

	async saveModel(model: HIIDespatchType) {
		await super.saveModel(model);

		model.cargoId = this.cargoId;
		model.cargoPortId = this.cargoPortId;
		model.amount = this.amount;
		model.terms = this.terms;
		model.hours = this.hours;
		model.rate = this.rate;
		model.type = this.type;
		model.demurrageDespatchInterval = this.demurrageDespatchInterval;
		model.timeAllowedInterval = this.timeAllowedInterval;
		model.isSettlement = this.isSettlement;

		await model.save();
	}

	_getTemplateItemParams() {
		const rowStart = `Despatch, ${this.terms}`;
		const title = this.withChangeSuffix(rowStart);
		const neoTitle = this.terms === LaytimeTerms.NON_REVERSIBLE && this.port?.name != null ?
			this.withChangeSuffix(`${rowStart}, ${this.port.name}`) :
			title;
		const rateUnit = this.getRateUnitLabel();
		let timeGained = `${this.getTimeValue()} ${this.getTimeUnit()} gained`;
		let descriptionDetails;

		if (this.isSettlement) {
			descriptionDetails = this?.port?.name ?? '';
			timeGained = '';
		} else {
			switch (this.terms) {
				case LaytimeTerms.REVERSIBLE:
					descriptionDetails = `@ ${formatCurrency(round(this.rate, 2),
						this.fixtureCurrency)
					} ${rateUnit}`;
					break;

				case LaytimeTerms.NON_REVERSIBLE:
					descriptionDetails = `${this.type} in ${this.port?.name}`;
					break;

				case LaytimeTerms.AVERAGED:
					descriptionDetails = `${timeGained} ${this.type} @ ${formatCurrency(round(this.rate, 2),
						this.fixtureCurrency)
					} ${rateUnit}`;
					timeGained = '';
					break;

				default:
					descriptionDetails = '';
			}
		}
		
		return {
			[InvoiceTemplates.CLASSIC]: {
				columns: [
					`${title}`,
					`${timeGained} ${descriptionDetails}`,
				],
				children: this.laytimeDetails ?? undefined,
			},
			[InvoiceTemplates.NEO]: {
				columns: [
					`${neoTitle}`,
					...(!this.isSettlement ? [`${this.getTimeValue()}`] : ['1']),
					...(!this.isSettlement ? [`${this.getTimeUnit()} lost`] : ['']),
					...(!this.isSettlement ? 
						[`${formatCurrency(this.rate, this.fixtureCurrency, { hideSymbol: true })} ${rateUnit}`] :
						[`${formatCurrency(this.amount, this.fixtureCurrency, { hideSymbol: true })} `]),
				],
			},
		};
	}
}

HireInvoiceItem.addItemClass(HIIDespatch);

export default HIIDespatch;
